// Marcas: o acesso cross-org do diretor de plataforma e os relatórios por
// marca. Um módulo por domínio do back — a fronteira HTTP continua sendo uma só
// (./client.js), só deixou de ser um arquivo único de 4.7k linhas.

import { api, toApiError } from "./client.js";

// --- Marcas (acesso cross-org do diretor) ---
// Espelha a feature `brands` do back (/v1/brands). Um diretor de plataforma
// (director em ao menos uma org) enxerga TODAS as marcas e pode se auto-adicionar
// a qualquer uma como director.

// Uma marca na listagem cross-org. `isMember`/`myRole`: se o diretor já participa
// e com qual papel — a UI usa para mostrar "trocar" vs "criar acesso e trocar".
export interface BrandSummary {
  id: string;
  name: string;
  slug: string;
  isMember: boolean;
  myRole: string | null;
  // Carimbo da desativação (ISO) ou null se a marca está no ar. Só vem
  // preenchido na listagem cross-org do diretor de plataforma (GET /v1/brands):
  // para o membro comum a marca desativada simplesmente não aparece na lista.
  // Ausente em respostas de um back anterior à desativação — trate como null.
  deactivatedAt?: string | null;
}

// GET /v1/brands/mine — somente memberships ativas do usuário atual. Esta é a
// fonte segura para vendedores e para reparar activeOrganizationId obsoleto.
export async function getMyBrands(): Promise<BrandSummary[]> {
  const { data, error } = await api<{ brands: BrandSummary[] }>("/v1/brands/mine");
  if (error) throw toApiError("GET /v1/brands/mine", error);
  return data.brands;
}

// GET /v1/brands — todas as marcas. Retorna null em 403 (solicitante não é
// diretor de plataforma): ausência honesta, não erro — a UI cai no modo padrão.
export async function getBrands(): Promise<BrandSummary[] | null> {
  const { data, error } = await api<{ brands: BrandSummary[] }>("/v1/brands");
  if (error) {
    if (error.status === 403) return null;
    throw toApiError("GET /v1/brands", error);
  }
  return data.brands;
}

// POST /v1/brands — cria uma marca para uma company grupo pelo painel da
// plataforma. O back já cria a filiação do diretor e devolve a marca pronta
// para ser selecionada.
export async function createBrand(name: string): Promise<BrandSummary> {
  const path = "/v1/brands";
  const { data, error } = await api<BrandSummary>(path, {
    method: "POST",
    body: { name },
  });
  if (error) throw toApiError(`POST ${path}`, error);
  return data;
}

// Métricas de uma fatia do relatório: uma marca, ou um vendedor dentro dela.
// successes/successRate vêm do CRM (conversões confirmadas ÷ leads
// trabalhados), não mais da tabulação do vendedor ÷ tentativas — mesma
// virada de fonte da Task 6 no back. losses/workedLeads são novos.
// workedLeads NÃO soma entre fatias (um lead trabalhado por dois vendedores
// contaria duas vezes): o back já manda o valor certo em cada nível
// (marca/vendedor/totals), calculado à parte — nunca recalcular no front.
export interface ReportMetrics {
  attempts: number;
  completed: number;
  successes: number;
  noAnswer: number;
  failed: number;
  totalDurationSec: number;
  avgDurationSec: number | null;
  // Leads marcados como perda no CRM no período.
  losses: number;
  // Leads distintos com >=1 ligação no período (denominador de successRate).
  workedLeads: number;
  // successes / workedLeads, já arredondado em pontos percentuais pelo back
  // (40 = 40%).
  successRate: number;
}

export interface BrandRow extends ReportMetrics {
  organizationId: string;
  name: string;
  slug: string;
}

// Desfecho já AGREGADO por rótulo pelo back (cada marca tem a própria cópia das
// tabulações, então somar por label é o que torna o consolidado consolidado).
export interface OutcomeRow {
  label: string;
  category: string;
  count: number;
}

export interface AllBrandsReport {
  totals: {
    brands: number;
    attempts: number;
    completed: number;
    successes: number;
    noAnswer: number;
    failed: number;
    totalDurationSec: number;
    avgDurationSec: number | null;
    // Mesma ressalva de ReportMetrics: workedLeads aqui já é o total de leads
    // distintos de TODAS as marcas do recorte — não é soma das marcas.
    losses: number;
    workedLeads: number;
  };
  brands: BrandRow[];
  outcomes: OutcomeRow[];
}

// Detalhe de UMA marca (a gaveta do relatório geral): totais da marca, quebra
// por vendedor e desfechos dela. `userId` null = ligações sem responsável.
export interface BrandDetailReport {
  brand: BrandRow;
  sellers: Array<
    ReportMetrics & { userId: string | null; name: string | null; email: string | null }
  >;
  outcomes: OutcomeRow[];
}

// GET /v1/reports/all-brands — agregado de todas as marcas, exclusivo do
// diretor de plataforma (director em ao menos uma organização).
// `organizationIds`: ids separados por vírgula (recorte do dialog de marcas).
// Ausente = todas. O back aplica o recorte na tabela, nos totais e nos desfechos.
export async function getAllBrandsReport(
  query: { from?: string; to?: string; organizationIds?: string } = {},
) {
  const { data, error } = await api<AllBrandsReport>("/v1/reports/all-brands", { query });
  if (error) throw toApiError("GET /v1/reports/all-brands", error);
  return data;
}

// GET /v1/reports/all-brands/:organizationId — detalhe de uma marca (gaveta da
// tela). Só período: o recorte de marcas não se aplica a uma marca só.
export async function getBrandDetailReport(
  organizationId: string,
  query: { from?: string; to?: string } = {},
) {
  const path = `/v1/reports/all-brands/${organizationId}`;
  const { data, error } = await api<BrandDetailReport>(path, { query });
  if (error) throw toApiError(`GET ${path}`, error);
  return data;
}

// POST /v1/brands/:id/join — cria (ou promove para director) o acesso do diretor
// à marca. Idempotente no back. `joined` = true quando a associação foi criada.
export async function joinBrand(
  organizationId: string,
): Promise<{ organizationId: string; role: string; joined: boolean }> {
  const { data, error } = await api<{
    organizationId: string;
    role: string;
    joined: boolean;
  }>(`/v1/brands/${organizationId}/join`, { method: "POST" });
  if (error) throw toApiError(`POST /v1/brands/${organizationId}/join`, error);
  return data;
}

// DELETE /v1/brands/:id — "excluir marca" da tela de identidade. É DESATIVAÇÃO
// no back: carimba a org e as filiações, some do app e dá para voltar atrás;
// nenhum lead, ligação ou gravação é apagado. Só o `director` da PRÓPRIA marca
// (403 para gerente, 404 para quem não é membro, 409 se já estava desativada).
// `members` = quantas filiações foram carimbadas junto — quem perdeu o acesso.
export async function deactivateBrand(
  organizationId: string,
): Promise<{ organizationId: string; members: number }> {
  const path = `/v1/brands/${organizationId}`;
  const { data, error } = await api<{ organizationId: string; members: number }>(path, {
    method: "DELETE",
  });
  if (error) throw toApiError(`DELETE ${path}`, error);
  return data;
}

// POST /v1/brands/:id/reactivate — desfaz a desativação. Só diretor de
// plataforma: desativar carimba TODAS as filiações, então depois dela não
// existe mais um diretor da marca para pedir a volta.
export async function reactivateBrand(
  organizationId: string,
): Promise<{ organizationId: string; members: number }> {
  const path = `/v1/brands/${organizationId}/reactivate`;
  const { data, error } = await api<{ organizationId: string; members: number }>(path, {
    method: "POST",
  });
  if (error) throw toApiError(`POST ${path}`, error);
  return data;
}

// DELETE /v1/brands/:id/permanent — exclusão DEFINITIVA, o oposto da de cima.
// A marca some do banco com leads, ligações, gravações, filas, campanhas e
// chaves de API. O back só aceita marca JÁ excluída (409 se ainda estiver no
// ar) e só do diretor de plataforma. Não tem reversão.
export interface BrandPurgeResult {
  organizationId: string;
  total: number;
  tabelas: Record<string, number>;
  ramais: number;
}

export async function purgeBrand(organizationId: string): Promise<BrandPurgeResult> {
  const path = `/v1/brands/${organizationId}/permanent`;
  const { data, error } = await api<BrandPurgeResult>(path, { method: "DELETE" });
  if (error) throw toApiError(`DELETE ${path}`, error);
  return data;
}
